import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import {
    apiProjectGet, apiProjectNote, apiProjectMilestone, apiProjectUpdate,
} from "@/lib/api";

import PortalNav from "@/components/portal/PortalNav";
import GridBackground from "@/components/primitives/GridBackground";
import BottomTabNav from "@/components/portal/BottomTabNav";
import HudFrame from "@/components/primitives/HudFrame";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function timeAgo(iso) {
    try {
        const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
        if (s < 60) return "just now";
        if (s < 3600) return `${Math.round(s/60)}m ago`;
        if (s < 86400) return `${Math.round(s/3600)}h ago`;
        return `${Math.round(s/86400)}d ago`;
    } catch { return ""; }
}

const STATUS_OPTS = ["active", "paused", "completed", "archived"];

export default function ProjectDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user, loading: authLoading, openAuthModal } = useAuth();
    const [project, setProject] = useState(null);
    const [notes, setNotes] = useState("");
    const [savedAt, setSavedAt] = useState(null);
    const [milestone, setMilestone] = useState("");
    const saveTimer = useRef(null);

    useEffect(() => { if (!authLoading && !user) openAuthModal("login"); }, [authLoading, user, openAuthModal]);

    useEffect(() => {
        if (!user || !id) return;
        (async () => {
            try {
                const p = await apiProjectGet(id);
                setProject(p);
                setNotes(p.notes || "");
            } catch (e) { toast.error("Failed to load project"); }
        })();
    }, [user, id]);

    // Autosave notes every 3s on change
    const onNotesChange = (v) => {
        setNotes(v);
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            try {
                await apiProjectNote(id, v);
                setSavedAt(new Date().toISOString());
            } catch { /* swallow */ }
        }, 3000);
    };

    const addMilestone = async () => {
        if (!milestone.trim()) return;
        try {
            const p = await apiProjectMilestone(id, milestone.trim());
            setProject(p);
            setMilestone("");
            toast.success("Milestone added");
        } catch (e) { toast.error("Failed"); }
    };

    const setStatus = async (s) => {
        try { const p = await apiProjectUpdate(id, { status: s }); setProject(p); }
        catch (e) { toast.error("Failed"); }
    };

    if (!user || !project) {
        return <div className="min-h-screen flex items-center justify-center bg-tca-deep"><Loader2 className="text-tca-cyan animate-spin" /></div>;
    }

    const refs = new Set(project.doctrine_refs || []);
    const milestones = project.progression?.milestones || [];
    const activity = project.activity || [];

    return (
        <div data-testid="project-detail-page" className="relative min-h-screen bg-tca-deep text-tca-ink overflow-x-hidden pb-20 lg:pb-0">
            <GridBackground fine />
            <PortalNav />

            <main className="relative z-10 max-w-[1280px] mx-auto px-5 sm:px-8 py-8">
                <button onClick={() => nav("/portal/projects")} data-testid="project-back" className="inline-flex items-center gap-2 font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-ink-muted hover:text-tca-cyan">
                    <ArrowLeft size={12} /> Back to projects
                </button>

                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-5 flex items-baseline justify-between gap-4 flex-wrap">
                    <div>
                        <div className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-cyan/80">// active build</div>
                        <h1 data-testid="project-name" className="mt-2 font-display text-[36px] sm:text-[48px] leading-[1.0] tracking-[-0.025em]">
                            {project.name}
                        </h1>
                        <p className="text-tca-ink-muted text-sm mt-1">{project.description || "—"}</p>
                    </div>
                    <select
                        value={project.status} onChange={(e) => setStatus(e.target.value)}
                        data-testid="project-status-select"
                        className="bg-tca-bg-panel/65 border border-tca-cyan/22 text-tca-ink-muted px-3 py-2 font-mono-tight text-[10px] uppercase tracking-[0.22em] rounded-full focus:outline-none focus:border-tca-cyan/55"
                    >
                        {STATUS_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </motion.div>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
                    {/* Left column */}
                    <div className="space-y-6">
                        <HudFrame bracketColor="var(--tca-cyan-core)" bracketSize={12} borderTint="rgba(0,219,255,0.18)" className="bg-tca-bg-panel/55 backdrop-blur-md" innerClassName="p-5">
                            <div className="flex items-center justify-between mb-3">
                                <span className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-cyan/85">// notes</span>
                                <span className="font-mono-tight text-[9px] uppercase tracking-[0.22em] text-tca-ink-muted">
                                    {savedAt ? `saved · ${timeAgo(savedAt)}` : "autosave on"}
                                </span>
                            </div>
                            <textarea
                                value={notes} onChange={(e) => onNotesChange(e.target.value)}
                                data-testid="project-notes-editor"
                                rows={14}
                                placeholder="Markdown-friendly. Autosaves every 3s while idle."
                                className="w-full bg-tca-bg-deep/40 border border-tca-ink-dim/40 focus:border-tca-cyan/55 focus:outline-none text-tca-ink p-4 font-mono-tight text-sm leading-relaxed resize-y"
                            />
                        </HudFrame>

                        <HudFrame bracketColor="var(--tca-cyan-core)" bracketSize={12} borderTint="rgba(0,219,255,0.18)" className="bg-tca-bg-panel/55 backdrop-blur-md" innerClassName="p-5">
                            <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-cyan/85 mb-3">// doctrine references</div>
                            <div className="flex flex-wrap gap-1.5" data-testid="project-doctrine-refs">
                                {LETTERS.map((L) => {
                                    const on = refs.has(L);
                                    return (
                                        <span key={L} className={`inline-flex items-center justify-center w-7 h-7 font-mono-tight text-[10px] font-semibold border ${on ? "border-tca-cyan text-tca-cyan-hi bg-tca-cyan/10 shadow-[0_0_8px_rgba(0,219,255,0.3)]" : "border-tca-ink-dim/40 text-tca-ink-muted/30"}`}>
                                            {L}
                                        </span>
                                    );
                                })}
                            </div>
                            {project.linked_modules?.length > 0 && (
                                <div className="mt-5">
                                    <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-cyan/85 mb-2">// linked modules</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {project.linked_modules.map((m) => (
                                            <span key={m} className="font-mono-tight text-[10px] uppercase tracking-[0.18em] text-tca-cyan/85 border border-tca-cyan/35 px-2.5 py-1 rounded-full">{m.replace("AGENT_","")}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </HudFrame>
                    </div>

                    {/* Right column */}
                    <aside className="space-y-6">
                        <HudFrame bracketColor="var(--tca-cyan-core)" bracketSize={12} borderTint="rgba(0,219,255,0.18)" className="bg-tca-bg-panel/55 backdrop-blur-md" innerClassName="p-5">
                            <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-cyan/85 mb-3">// milestones</div>
                            <div className="space-y-1.5 mb-3" data-testid="project-milestones-list">
                                {milestones.length === 0 ? (
                                    <div className="text-tca-ink-muted text-xs">No milestones yet.</div>
                                ) : (
                                    milestones.map((m, i) => (
                                        <div key={i} className="text-[12px] text-tca-ink">
                                            <span className="font-mono-tight text-[9px] text-tca-cyan/75 mr-2">{timeAgo(m.at)}</span>
                                            {m.name}
                                        </div>
                                    ))
                                )}
                            </div>
                            <div className="flex gap-1.5">
                                <input
                                    value={milestone} onChange={(e) => setMilestone(e.target.value)}
                                    data-testid="project-milestone-input"
                                    placeholder="Add milestone…"
                                    className="flex-1 bg-tca-bg-deep/40 border border-tca-ink-dim/40 focus:border-tca-cyan/55 focus:outline-none text-tca-ink px-3 py-1.5 text-xs"
                                />
                                <button onClick={addMilestone} data-testid="project-milestone-add" className="px-3 py-1.5 border border-tca-cyan/55 text-tca-cyan hover:border-tca-cyan">
                                    <Plus size={12} />
                                </button>
                            </div>
                        </HudFrame>

                        <HudFrame bracketColor="var(--tca-cyan-core)" bracketSize={12} borderTint="rgba(0,219,255,0.18)" className="bg-tca-bg-panel/55 backdrop-blur-md" innerClassName="p-5">
                            <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-cyan/85 mb-3">// activity</div>
                            <div className="space-y-1 font-mono-tight text-[10px] uppercase tracking-[0.22em]" data-testid="project-activity-timeline">
                                {activity.length === 0 ? (
                                    <div className="text-tca-ink-muted normal-case tracking-normal text-xs">No activity yet.</div>
                                ) : (
                                    activity.slice().reverse().map((a, i) => (
                                        <div key={i} className="text-tca-ink-muted">
                                            <span className="text-tca-cyan/85">// {timeAgo(a.ts)}</span>
                                            {" — "}
                                            <span className="text-tca-cyan/65">{a.type}</span>
                                            {a.label && <>: <span className="text-tca-ink normal-case tracking-tight">{a.label}</span></>}
                                        </div>
                                    ))
                                )}
                            </div>
                        </HudFrame>
                    </aside>
                </div>
            </main>

            <BottomTabNav />
        </div>
    );
}
